import React, { useState, useEffect, useRef } from 'react'
import { X, Copy, ChevronDown, ChevronUp, Search } from 'lucide-react'
import { Typography } from '../../interfaces/typography'
import { useProvisionedTeams } from '../../lib/redux/provisioned/hooks'
import { useTeamCopy } from '../../hooks/useTeamCopy'

interface TeamSearchDropdownProps {
  teams: Array<{ team_id: string; team_name: string }>
  selectedTeamId: string
  onTeamSelect: (teamId: string) => void
  placeholder?: string
  disabled?: boolean
}

const TeamSearchDropdown: React.FC<TeamSearchDropdownProps> = ({
  teams,
  selectedTeamId,
  onTeamSelect,
  placeholder = "Search teams...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedTeam = teams.find(team => team.team_id === selectedTeamId)
  
  const filteredTeams = searchQuery.length < 2 
    ? [] 
    : teams.filter(team => 
        team.team_name.toLowerCase().includes(searchQuery.toLowerCase())
      )

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleTeamSelect = (teamId: string) => {
    onTeamSelect(teamId)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchQuery('')
      }
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        disabled={disabled}
        className="flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        <span className={selectedTeam ? "text-gray-900" : "text-gray-500"}>
          {selectedTeam ? selectedTeam.team_name : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
          <div className="p-2 bg-gray-50 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type at least 2 characters..."
                className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredTeams.length > 0 ? (
              filteredTeams.map((team) => (
                <button
                  key={team.team_id}
                  onClick={() => handleTeamSelect(team.team_id)}
                  className="block w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none text-sm text-gray-900"
                  style={{ color: '#111827' }}
                >
                  {team.team_name}
                </button>
              ))
            ) : searchQuery.length >= 2 ? (
              <div className="px-3 py-4 text-center">
                <Typography variant="smallParagraph" color="#9ca3af">
                  No teams found matching "{searchQuery}"
                </Typography>
              </div>
            ) : (
              <div className="px-3 py-4 text-center">
                <Typography variant="smallParagraph" color="#9ca3af">
                  Type at least 2 characters to search {teams.length} teams
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export const TeamCreationModal: React.FC = () => {
  const {
    isCreationModalOpen,
    isProvisioning,
    error,
    createNewTeam,
    closeCreationModal,
    selectTeam,
    availableTeams
  } = useProvisionedTeams()
  
  const { copyTeamConfiguration, isLoading: isCopying, progress } = useTeamCopy()
  
  const [teamName, setTeamName] = useState('')
  const [showResult, setShowResult] = useState(false)
  const [createdTeam, setCreatedTeam] = useState<{ name: string; id: string } | null>(null)
  const [showCopyOptions, setShowCopyOptions] = useState(true) // Show by default
  const [creationMode, setCreationMode] = useState<'blank' | 'copy'>('blank')
  const [selectedSourceTeam, setSelectedSourceTeam] = useState<string>('')
  const [copyOptions, setCopyOptions] = useState({
    includePrompts: true,
    includeVoiceBots: true,
    includeChatBots: true,
    includeStateCollections: true,
    includeWebhooks: true
  })
  const [isCopyingConfiguration, setIsCopyingConfiguration] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Reset state when modal opens
  useEffect(() => {
    if (isCreationModalOpen) {
      setTeamName('')
      setShowResult(false)
      setCreatedTeam(null)
      setShowCopyOptions(true)
      setCreationMode('blank')
      setSelectedSourceTeam('')
      setIsCopyingConfiguration(false)
      setShowConfirmation(false)
    }
  }, [isCreationModalOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamName.trim()) return

    // Show confirmation dialog first
    setShowConfirmation(true)
  }

  const handleConfirmCreate = async () => {
    setShowConfirmation(false)
    const result = await createNewTeam(teamName.trim())
    if (result) {
      setCreatedTeam({ name: teamName.trim(), id: result.team_id })
      
      // If user wants to copy from another team, do it now
      if (creationMode === 'copy' && selectedSourceTeam) {
        setIsCopyingConfiguration(true)
        await copyTeamConfiguration(selectedSourceTeam, result.team_id, copyOptions)
        setIsCopyingConfiguration(false)
      }
      
      setShowResult(true)
    }
  }

  const handleClose = () => {
    if (closeCreationModal) {
      closeCreationModal()
    }
  }

  const handleLoginAsTeam = () => {
    if (createdTeam) {
      selectTeam(createdTeam.id)
      handleClose()
    }
  }

  if (!isCreationModalOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70] backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true">
        <div className="p-6">
          {showConfirmation ? (
            // Confirmation Dialog
            <div>
              <div className="flex justify-between items-center mb-6">
                <Typography variant="h3" color="#000000">
                  Confirm Team Creation
                </Typography>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <Typography variant="mediumParagraph" color="#000000" fontWeight="600" className="mb-2">
                  You are about to create:
                </Typography>
                <Typography variant="mediumParagraph" color="#374151" className="mb-2">
                  Team Name: <strong>{teamName}</strong>
                </Typography>
                <Typography variant="mediumParagraph" color="#374151" className="mb-2">
                  Type: {creationMode === 'copy' ? 'Copy from existing team' : 'Blank team'}
                </Typography>
                {creationMode === 'copy' && selectedSourceTeam && (
                  <Typography variant="mediumParagraph" color="#374151" className="mb-2">
                    Source Team: <strong>{availableTeams.find(t => t.team_id === selectedSourceTeam)?.team_name}</strong>
                  </Typography>
                )}
              </div>

              <Typography variant="smallParagraph" color="#6b7280" className="mb-6">
                Are you sure you want to create this team? This action cannot be undone.
              </Typography>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <Typography variant="mediumParagraph" color="#374151">
                    Cancel
                  </Typography>
                </button>
                <button
                  onClick={handleConfirmCreate}
                  disabled={isProvisioning || isCopyingConfiguration}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Typography variant="mediumParagraph" color="#ffffff">
                    Yes, Create Team
                  </Typography>
                </button>
              </div>
            </div>
          ) : !showResult ? (
            // Creation Form
            <div>
              <div className="flex justify-between items-center mb-6">
                <Typography variant="h3" color="#000000">
                  Create New Team
                </Typography>
                <button
                  onClick={handleClose}
                  disabled={isProvisioning}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <Typography variant="smallParagraph" color="#dc2626">
                    {error}
                  </Typography>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Typography variant="mediumParagraph" color="#000000" className="block mb-3">
                    Team Name
                  </Typography>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter your team name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500 mb-2"
                    disabled={isProvisioning}
                    autoFocus
                  />
                  <Typography variant="smallParagraph" color="#6b7280" className="text-sm">
                    This will be your team's display name in Trellus
                  </Typography>
                </div>

                {/* Team Creation Mode Selection */}
                {availableTeams && availableTeams.length > 0 && (
                  <div className="space-y-4">
                    <Typography variant="mediumParagraph" color="#000000" className="block">
                      How would you like to create your team?
                    </Typography>
                    
                    {/* Mode Selection Radio Buttons */}
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
                        <input
                          type="radio"
                          name="creationMode"
                          value="blank"
                          checked={creationMode === 'blank'}
                          onChange={(e) => setCreationMode(e.target.value as 'blank' | 'copy')}
                          className="mt-1 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div>
                          <Typography variant="mediumParagraph" color="#000000" fontWeight="600">
                            Create blank team
                          </Typography>
                          <Typography variant="smallParagraph" color="#6b7280">
                            Start fresh with no pre-configured prompts, bots, or settings
                          </Typography>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
                        <input
                          type="radio"
                          name="creationMode"
                          value="copy"
                          checked={creationMode === 'copy'}
                          onChange={(e) => setCreationMode(e.target.value as 'blank' | 'copy')}
                          className="mt-1 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div>
                          <Typography variant="mediumParagraph" color="#000000" fontWeight="600">
                            Copy from existing team
                          </Typography>
                          <Typography variant="smallParagraph" color="#6b7280">
                            Copy configuration from another team (prompts, bots, state collections, webhooks)
                          </Typography>
                        </div>
                      </label>
                    </div>

                    {/* Copy Configuration Options */}
                    {creationMode === 'copy' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                        <div>
                          <Typography variant="smallParagraph" color="#374151" className="block mb-2" fontWeight="600">
                            Select team to copy from:
                          </Typography>
                          {availableTeams.length > 30 ? (
                            <TeamSearchDropdown 
                              teams={availableTeams}
                              selectedTeamId={selectedSourceTeam}
                              onTeamSelect={setSelectedSourceTeam}
                              placeholder="Search and select team..."
                              disabled={isProvisioning}
                            />
                          ) : (
                            <select
                              value={selectedSourceTeam}
                              onChange={(e) => setSelectedSourceTeam(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-gray-900"
                              style={{ color: '#111827' }}
                              disabled={isProvisioning}
                              required
                            >
                              <option value="" style={{ color: '#6b7280' }}>Choose a team...</option>
                              {availableTeams.map((team) => (
                                <option key={team.team_id} value={team.team_id} style={{ color: '#111827' }}>
                                  {team.team_name}
                                </option>
                              ))}
                            </select>
                          )}
                          {!selectedSourceTeam && (
                            <Typography variant="smallParagraph" color="#dc2626" className="mt-1">
                              Please select a team to copy from
                            </Typography>
                          )}
                        </div>

                        {selectedSourceTeam && (
                          <div>
                            <Typography variant="smallParagraph" color="#374151" className="block mb-2" fontWeight="600">
                              What to copy:
                            </Typography>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={copyOptions.includeStateCollections}
                                  onChange={(e) => setCopyOptions(prev => ({ ...prev, includeStateCollections: e.target.checked }))}
                                  className="text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Typography variant="smallParagraph" color="#374151">
                                  State Collections
                                </Typography>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={copyOptions.includeWebhooks}
                                  onChange={(e) => setCopyOptions(prev => ({ ...prev, includeWebhooks: e.target.checked }))}
                                  className="text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Typography variant="smallParagraph" color="#374151">
                                  Webhooks
                                </Typography>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={copyOptions.includePrompts}
                                  onChange={(e) => setCopyOptions(prev => ({ ...prev, includePrompts: e.target.checked }))}
                                  className="text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Typography variant="smallParagraph" color="#374151">
                                  Prompts
                                </Typography>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={copyOptions.includeVoiceBots}
                                  onChange={(e) => setCopyOptions(prev => ({ ...prev, includeVoiceBots: e.target.checked }))}
                                  className="text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Typography variant="smallParagraph" color="#374151">
                                  Voice Bots
                                </Typography>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={copyOptions.includeChatBots}
                                  onChange={(e) => setCopyOptions(prev => ({ ...prev, includeChatBots: e.target.checked }))}
                                  className="text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Typography variant="smallParagraph" color="#374151">
                                  Chat Bots
                                </Typography>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(isProvisioning || isCopyingConfiguration) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      <div className="flex-1">
                        <Typography variant="mediumParagraph" color="#92400e" fontWeight="600">
                          {isProvisioning ? 'Creating Team...' : 'Copying Configuration...'}
                        </Typography>
                        <Typography variant="smallParagraph" color="#92400e">
                          {isProvisioning 
                            ? 'Setting up your team workspace and Twilio integration'
                            : isCopyingConfiguration 
                              ? `${progress.currentStep} (${progress.completed}/${progress.total})`
                              : 'Copying team configuration'
                          }
                        </Typography>
                        {isCopyingConfiguration && progress.total > 0 && (
                          <div className="mt-2 bg-white rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isProvisioning || isCopyingConfiguration}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Typography variant="mediumParagraph" color="#374151">
                      Cancel
                    </Typography>
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !teamName.trim() || 
                      isProvisioning || 
                      isCopyingConfiguration ||
                      (creationMode === 'copy' && !selectedSourceTeam)
                    }
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Typography variant="mediumParagraph" color="#ffffff">
                      {isProvisioning ? 'Creating Team...' : 
                       isCopyingConfiguration ? 'Copying Configuration...' : 
                       creationMode === 'copy' ? 
                         `Create Team & Copy from ${availableTeams.find(t => t.team_id === selectedSourceTeam)?.team_name || 'Selected Team'}` : 
                         'Create Blank Team'}
                    </Typography>
                  </button>
                </div>
              </form>
            </div>
          ) : (
            // Success Result
            <div className="text-center">
              <div className="flex justify-end mb-6">
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <Typography variant="h3" color="#000000" className="mb-3">
                Team Created Successfully!
              </Typography>
              
              <Typography variant="mediumParagraph" color="#6b7280" className="mb-6">
                <strong>{createdTeam?.name}</strong> has been created{selectedSourceTeam ? ' with copied configuration' : ' as a blank team'} and is ready to use.
              </Typography>
              
              <button
                onClick={handleLoginAsTeam}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Typography variant="mediumParagraph" color="#ffffff">
                  Log in as Team
                </Typography>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 